import { useEditor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import {
  detectsBrokenBulletList,
  fixBrokenBulletListFromText,
} from '../../../services/pasteCleanup';
import sanitizeHTML from '../../../services/sanitizeHTML';
import { sanitizeFaqEditorHtml } from '../../faq/sanitizeFaqEditorHtml';
import { INLINE_RICH_TEXT_PRESETS, type InlineRichTextPreset } from './inlineRichTextPresets';

export function stripOuterParagraph(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html.trim();
  if (div.childNodes.length === 1 && div.firstElementChild?.tagName.toLowerCase() === 'p') {
    return div.firstElementChild.innerHTML;
  }
  return div.innerHTML;
}

/**
 * Owns the Tiptap editor instance and its surrounding state for InlineRichText:
 * focus tracking, syncing external `html` changes in when not focused, sanitising
 * on blur, and the "broken bullet list" paste-cleanup flow (detect on paste,
 * apply or dismiss via PasteCleanupBanner).
 */
export function useInlineRichTextEditor({
  html,
  unset,
  ariaLabel,
  onChange,
  onLiveChange,
  onFocus,
  preset = 'textSection',
}: {
  html: string;
  unset?: boolean;
  ariaLabel: string;
  onChange: (html: string) => void;
  onLiveChange?: (html: string) => void;
  onFocus?: () => void;
  preset?: InlineRichTextPreset;
}) {
  const [focused, setFocused] = useState(false);
  const [pasteCleanupAvailable, setPasteCleanupAvailable] = useState(false);
  const lastPastedText = useRef<string>('');
  const onLiveChangeRef = useRef(onLiveChange);
  onLiveChangeRef.current = onLiveChange;

  const editor = useEditor({
    extensions: INLINE_RICH_TEXT_PRESETS[preset].extensions,
    content: unset ? '' : html || '',
    editorProps: {
      attributes: { role: 'textbox', 'aria-label': ariaLabel, 'aria-multiline': 'true' },
      handlePaste(_view, event) {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (detectsBrokenBulletList(text)) {
          lastPastedText.current = text;
          setTimeout(() => setPasteCleanupAvailable(true), 50);
        }
        return false; // false = let TipTap handle the paste as usual
      },
    },
    onFocus: () => {
      setFocused(true);
      onFocus?.();
    },
    onUpdate: ({ editor: instance }) => {
      onLiveChangeRef.current?.(stripOuterParagraph(instance.getHTML()));
    },
    onBlur: ({ editor: instance }) => {
      setFocused(false);
      const value = stripOuterParagraph(instance.getHTML());
      onChange(preset === 'faq' ? sanitizeFaqEditorHtml(value) : sanitizeHTML(value));
    },
  });

  useEffect(() => {
    if (!editor) return;
    const next = unset ? '' : html || '';
    if (!editor.isFocused && stripOuterParagraph(editor.getHTML()) !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, html, unset]);

  const applyPasteCleanup = () => {
    if (!editor) return;
    const fixed = fixBrokenBulletListFromText(lastPastedText.current);
    editor.commands.setContent(fixed, { emitUpdate: false });
    onChange(sanitizeHTML(stripOuterParagraph(fixed)));
    setPasteCleanupAvailable(false);
  };

  return {
    editor,
    focused,
    pasteCleanupAvailable,
    applyPasteCleanup,
    dismissPasteCleanup: () => setPasteCleanupAvailable(false),
  };
}
